import {
  ref,
  computed,
  watch,
  Ref,
  onMounted,
  getCurrentInstance,
  inject,
} from "vue";
import { useRouter } from "vue-router";
import { PayJoyCardStatus } from "@/apps/credit-line/models/PayJoyCardStatus";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import {
  IAddressInfo,
  ICreditLineDetails,
} from "@/apps/credit-line/models/CreditLineDetails";
import { useCreditLineSummary } from "./useCreditLineSummary";
import { useCardStatus } from "./useCardStatus";
import { useCardDetails } from "./useCardDetails";
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";
import { useStore } from "vuex";
import {
  NeedFixAdressInfo,
  NeedFixPersonalAndAdressInfo,
} from "../models/ErrorsNeedFixUserData";
import useAsset from "@/apps/credit-line/helpers/useAsset";

declare const Android: AndroidInterface;

declare global {
  interface Window {
    creditLineApp?: {
      cardsInWallet?: (cardList: string[]) => void;
      biometricAuthCallback?: (success: number) => void;
    };
  }
}

export function useVirtualCardDetails(
  locale: Ref<string>,
  supportsAddToGoogleWallet: Ref<boolean>
): {
  disableCard: Ref<boolean | false>;
  payjoyCardStatus: Ref<PayJoyCardStatus | undefined>;
  alertMessage: Ref<string | undefined>;
  showAlert: Ref<boolean>;
  createVirtualCard: () => Promise<void>;
  closeVirtualCardCreationNotAllowedModal: () => void;
  alertIcon;
  alertType;
  alertClosable;
  delinquent;
  iFrameUrl;
  showCreate;
  showToggle;
  showDelete;
  disableCreate;
  showDetails;
  confirmModalDisplayed;
  addressInfo;
  shipmentId;
  toggleCardDetails;
  deleteCard;
  navToCalculator;
  navToCardIntro;
  closeModal;
  handleCardStatusToggle;
  addToWalletAsset;
  walletBtnType;
  addToWallet;
  showWalletBtn;
  showVirtualCardCreationNotAllowedModal: Ref<boolean>;
  redWarningIcon: string;
} {
  const router = useRouter();

  const store = useStore();

  // Virtual Card creation allowance
  const allowVirtualCardCreation = inject(
    "allowVirtualCardCreation",
    ref(false)
  );
  const showVirtualCardCreationNotAllowedModal = ref<boolean>(false);
  const redWarningIcon = useAsset("img/credit-line/red_warning_icon.png");

  const {
    summary,
    isLoading: loadingSummary,
    fetchSummary,
  } = useCreditLineSummary();

  const {
    payjoyCardStatus,
    disableCard,
    selectedCard,
    creditLineId,
    alertMessage,
    showAlert,
    toggleCardStatus,
    createVirtualCard,
  } = useCardStatus();
  const { iFrameUrl, showDetails, toggleCardDetails } = useCardDetails();

  const confirmModalDisplayed = ref<boolean>(false);
  const alertIcon = ref<string>("fa fa-exclamation-triangle");
  const alertType = ref<string>("alert-danger");
  const alertClosable = ref<boolean>(true);
  const delinquent = ref<boolean>(false);
  const addressInfo = ref<IAddressInfo>();
  let shipmentId: ICreditLineDetails["shipmentId"];

  const addToWalletAsset = computed(() =>
    locale.value.substring(0, 2) === "en"
      ? "img/credit-line/credit-card/add_to_google_wallet_en.png"
      : "img/credit-line/credit-card/add_to_google_wallet_es.png"
  );
  const listOfCardsInWallet = ref<string[]>([]);

  const disableBtn = computed(
    () => loadingSummary.value === true || delinquent.value === true
  );

  const availableCredit = computed(() =>
    Number(summary.value?.availableCredit ?? 0)
  );

  const userId = computed(() => Number(summary.value?.userId ?? null));

  const isCreatingVirtualCard = computed((): boolean => {
    return store.getters["creditLine/getIsCreatingVirtualCard"] !== false;
  });
  const showCreate = computed(
    () =>
      !!payjoyCardStatus.value &&
      [
        PayJoyCardStatus.NONE,
        PayJoyCardStatus.ELIGIBLE,
        PayJoyCardStatus.DISABLED,
      ].includes(payjoyCardStatus.value)
  );
  const disableCreate = computed(() => {
    if (isCreatingVirtualCard.value) {
      return true;
    }

    return disableBtn.value && showCreate.value;
  });

  const showToggle = computed(
    () =>
      !!payjoyCardStatus.value &&
      [PayJoyCardStatus.ACTIVE, PayJoyCardStatus.BLOCKED].includes(
        payjoyCardStatus.value
      )
  );

  const showDelete = computed(
    () =>
      !!payjoyCardStatus.value &&
      [PayJoyCardStatus.ACTIVE, PayJoyCardStatus.BLOCKED].includes(
        payjoyCardStatus.value
      )
  );

  const showWalletBtn = computed(
    () =>
      !!payjoyCardStatus.value &&
      [PayJoyCardStatus.ACTIVE, PayJoyCardStatus.BLOCKED].includes(
        payjoyCardStatus.value
      ) &&
      supportsAddToGoogleWallet
  );

  const triggerAnalyticsEvent = (eventName: string) =>
    logAnalyticsEvent(eventName, "CC_Details");

  const closeModal = () => {
    confirmModalDisplayed.value = false;
  };

  const deleteCard = () => {
    router.push({
      name: "CreditCardConfirmDeleteView",
      query: {
        deviceTag: AuthenticationHelper.getDeviceTag() as string,
        creditLineId: creditLineId.value,
        lastFour: selectedCard.value?.lastFour,
      },
    });
  };

  const navToCalculator = () => {
    if (disableCard.value === false) {
      triggerAnalyticsEvent(
        AnalyticEvents.CreditLine.CcCalculatorNavFromCardScreen
      );
      router.push({
        name: "CreditCardTransactionCalculator",
        query: {
          userId: userId.value,
          creditLineId: creditLineId.value,
          availableCredit: availableCredit.value,
          origin: "cc_main_screen",
        },
      });
    }
  };

  const navToCardIntro = () => {
    triggerAnalyticsEvent(AnalyticEvents.CreditLine.ClickHowtoUseVirtualCard);
    router.push({
      name: "CreditCardConfirmCreateView",
    });
  };

  const walletBtnType = computed(() => {
    if (supportsAddToGoogleWallet.value) {
      if (selectedCard.value) {
        if (listOfCardsInWallet.value.length > 0) {
          const selectedCardIsInWallet = listOfCardsInWallet.value.includes(
            selectedCard.value?.lastFour ?? ""
          );
          if (selectedCardIsInWallet) {
            return "added";
          } else {
            return "add";
          }
        } else {
          return "add";
        }
      }
      return "none";
    }

    return "none";
  });

  const cardsInWallet = (cardList: string[]) => {
    listOfCardsInWallet.value = cardList;
  };

  const getAddressForWallet = () => {
    const addressForWallet = {
      address1: summary.value?.address?.street_name ?? "",
      address2: summary.value?.address?.street_number ?? "",
      countryCode: summary.value?.country ?? "",
      locality: summary.value?.address?.neighborhood ?? "",
      administrativeArea: summary.value?.address?.region ?? "",
      name: summary.value?.customerName ?? "",
      phoneNumber: summary.value?.customerPhone ?? "",
      postalCode: summary.value?.address?.zip_code ?? "",
    };
    return JSON.stringify(addressForWallet);
  };

  const addToWallet = () => {
    Android.addToGoogleWallet(
      selectedCard.value?.externalCreditCardId ?? "",
      selectedCard.value?.lastFour ?? "",
      getAddressForWallet()
    );
  };

  if (getCurrentInstance()) {
    onMounted(async () => {
      window.creditLineApp = {
        cardsInWallet,
      };
    });
  }

  // Watch for changes in the summary
  watch(
    summary,
    (newSummary) => {
      if (newSummary) {
        if (isAndroid()) {
          setTimeout(() => Android.finishedLoading(), 500);
        }
      }
    },
    { immediate: true }
  );

  const handleCardStatusToggle = (status: "ACTIVE" | "BLOCKED") => {
    if (status === "BLOCKED") {
      triggerAnalyticsEvent(AnalyticEvents.CreditLine.CcClickTemporaryDisabled);
    }

    return toggleCardStatus(status);
  };

  const requestCreateCard = async () => {
    if (!allowVirtualCardCreation.value) {
      showVirtualCardCreationNotAllowedModal.value = true;
      return;
    }

    if (isCreatingVirtualCard.value) {
      return;
    }
    try {
      await createVirtualCard();

      const summary = await fetchSummary(true);

      const previousCreateVirtualCards =
        summary?.cards?.filter((card) => card?.type === "VIRTUAL") ?? [];

      if (previousCreateVirtualCards.length === 1) {
        void router.push({
          name: "CreditCardConfirmCreateView",
          query: { country: store.state?.creditLine?.summary?.country },
        });
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      const errorCode = e.response.data.errorCode;
      const cardType = "virtual";
      if (errorCode) {
        if (Object.values(NeedFixPersonalAndAdressInfo).includes(errorCode)) {
          void router.push({
            name: "CreditCardUserDataCaptureView",
            query: { type: cardType, extraAction: "createVirtualCard" },
          });
        } else if (Object.values(NeedFixAdressInfo).includes(errorCode)) {
          void router.push({
            name: "CreditCardUserDataCaptureView",
            query: {
              type: cardType,
              skipUserForm: "true",
              extraAction: "createVirtualCard",
            },
          });
        } else {
          void router.push({ name: "CreditCardVirtualView" });
        }
      }
    }
  };

  const closeVirtualCardCreationNotAllowedModal = () => {
    showVirtualCardCreationNotAllowedModal.value = false;
  };

  return {
    payjoyCardStatus,
    disableCard,
    showAlert,
    alertMessage,
    alertIcon,
    alertType,
    alertClosable,
    delinquent,
    iFrameUrl,
    showCreate,
    showToggle,
    showDelete,
    disableCreate,
    showDetails,
    confirmModalDisplayed,
    addressInfo,
    shipmentId,
    redWarningIcon,
    showVirtualCardCreationNotAllowedModal,
    closeVirtualCardCreationNotAllowedModal,
    addToWalletAsset,
    walletBtnType,
    addToWallet,
    showWalletBtn,
    createVirtualCard: requestCreateCard,
    toggleCardDetails: (newVal: boolean) =>
      toggleCardDetails(newVal, selectedCard.value?.lastFour ?? ""),
    deleteCard,
    navToCalculator,
    navToCardIntro,
    closeModal,
    handleCardStatusToggle,
  };
}
